import { formatDate } from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { IDurationObject } from "app/services/DurationService";
import { AboveAverage, BelowAverage } from "pro-features/components/Pill/AboveBelowAverage";

import { CompareValueCell } from "../CompareValueCell";

interface ITooltipRow {
    metric: string;
    value: string;
    aboveAverage: boolean | null;
}

interface IPeriodOverPeriodTooltipRow {
    metric: string;
    previousPeriodValue: string;
    currentPeriodValue: string;
    change: string;
}

interface IAverageTooltipRow {
    metric: string;
    value: string;
}

const createMetricColumn = <T extends { metric: string }>(): ColumnDefinition<T> => ({
    key: "1",
    title: translate("Metric"),
    selector: "metric",
    align: "left",
    size: ChartTooltip.LabelCellDefaultWidth,
    render: ({ metric }) => {
        return <ChartTooltip.LabelCell label={metric} />;
    },
});

const createValueColumn = <T extends { value: string }>(): ColumnDefinition<T> => ({
    key: "2",
    title: translate("Value"),
    selector: "value",
    align: "right",
    size: ChartTooltip.ChangeCellDefaultWidth,
    render: ({ value }) => {
        return <ChartTooltip.ValueCell value={value} />;
    },
    borderRight: true,
});

const averageColumn: ColumnDefinition<ITooltipRow> = {
    key: "3",
    title: "",
    selector: "aboveAverage",
    align: "right",
    size: ChartTooltip.LabelCellDefaultWidth,
    render: ({ aboveAverage }) => {
        return aboveAverage === null ? (
            <Flex justifyContent="center">-</Flex>
        ) : (
            <Flex justifyContent="right">{aboveAverage ? <AboveAverage /> : <BelowAverage />}</Flex>
        );
    },
};

const previousPeriodValueColumn = (
    filters: IDurationObject<string>,
    isHighlighted: boolean,
): ColumnDefinition<IPeriodOverPeriodTooltipRow> => {
    return {
        key: "previousPeriodValue",
        title: formatDate(filters.compareFrom, filters.compareTo),
        selector: "previousPeriodValue",
        align: "right",
        size: ChartTooltip.LabelCellDefaultWidth,
        borderRight: true,
        render: ({ previousPeriodValue }) => {
            return (
                <CompareValueCell
                    value={previousPeriodValue}
                    isHighlighted={isHighlighted}
                />
            );
        },
    };
};

const currentPeriodValueColumn = (
    filters: IDurationObject<string>,
    isHighlighted: boolean,
): ColumnDefinition<IPeriodOverPeriodTooltipRow> => {
    return {
        key: "currentPeriodValue",
        title: formatDate(filters.from, filters.to),
        selector: "currentPeriodValue",
        align: "right",
        size: ChartTooltip.LabelCellDefaultWidth,
        borderRight: true,
        render: ({ currentPeriodValue }) => {
            return (
                <CompareValueCell
                    value={currentPeriodValue}
                    isHighlighted={isHighlighted}
                />
            );
        },
    };
};

const periodOverPeriodChangeColumn: ColumnDefinition<IPeriodOverPeriodTooltipRow> = {
    key: "change",
    get title() {
        return translate("common.tooltip.change");
    },
    selector: "change" as const,
    align: "right",
    size: ChartTooltip.ChangeCellDefaultWidth,
    render: ({ change }) => {
        return (
            <ChartTooltip.ChangeCell
                value={change}
                isDecrease={Number(change.replace(/[^0-9.-]/g, "")) < 0}
                // @ts-expect-error FIXME: change is string but compared to number 0 — pre-existing behavior
                unsigned={change === 0 || change === "-" || change === translate("apps.common.na")}
            />
        );
    },
};

export const getColumnsDefinition = () => {
    return [createMetricColumn<ITooltipRow>(), createValueColumn<ITooltipRow>(), averageColumn];
};

export const getPeriodOverPeriodColumnsDefinition = (filters: IDurationObject<string>, isPreviousPeriod: boolean) => {
    return [
        createMetricColumn<IPeriodOverPeriodTooltipRow>(),
        previousPeriodValueColumn(filters, isPreviousPeriod),
        currentPeriodValueColumn(filters, !isPreviousPeriod),
        periodOverPeriodChangeColumn,
    ];
};

export const getAverageColumnsDefinition = () => {
    return [
        { ...createMetricColumn<IAverageTooltipRow>(), borderRight: true },
        { ...createValueColumn<IAverageTooltipRow>(), borderRight: false },
    ];
};
